/**
 * Select all / deselect all toggle for MultiTagFilterDropdown — one button that
 * activates every visible tag, or clears them when they are all active already.
 * Acts only on the options the search currently shows; the "no tag" row is
 * never included. For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { CheckCheck } from 'lucide-react';

export interface MultiTagSelectAllRowProps {
  values: string[];
  /** Tag options currently visible in the list (search already applied). */
  visibleOptions: string[];
  onChange: (values: string[]) => void;
}

export const MultiTagSelectAllRow: FC<MultiTagSelectAllRowProps> = ({
  values,
  visibleOptions,
  onChange,
}) => {
  if (visibleOptions.length === 0) return null;

  const selected = new Set(values);
  const allSelected = visibleOptions.every((tag) => selected.has(tag));

  const toggleAll = () => {
    if (allSelected) {
      const visible = new Set(visibleOptions);
      onChange(values.filter((v) => !visible.has(v)));
    } else {
      onChange([...values, ...visibleOptions.filter((tag) => !selected.has(tag))]);
    }
  };

  return (
    <div className="border-b border-border p-1">
      <button
        type="button"
        className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
        onClick={toggleAll}
      >
        <CheckCheck className="h-3.5 w-3.5 shrink-0" aria-hidden />
        {allSelected ? 'Deselect all' : `Select all (${visibleOptions.length})`}
      </button>
    </div>
  );
};
